import type { MessageRead } from '../client'

/** A stored message's `content` is either plain text or the block list the model produced.
 * Parsing lives here so the components stay presentational. */

export type ToolUse = { id: string; name: string; displayName?: string }
export type ToolResult = { content: string; isError: boolean }

export type Block = Record<string, unknown>

function parse(content: MessageRead['content']): unknown {
  if (typeof content !== 'string') return content
  try {
    return JSON.parse(content)
  } catch {
    return content
  }
}

export function messageBlocks(content: MessageRead['content']): Block[] {
  const parsed = parse(content)
  return Array.isArray(parsed) ? (parsed as Block[]) : []
}

function joinType(content: MessageRead['content'], type: string, key: string) {
  return messageBlocks(content)
    .filter((block) => block.type === type && typeof block[key] === 'string')
    .map((block) => block[key] as string)
    .join('\n')
    .trim()
}

export function messageText(content: MessageRead['content']) {
  const parsed = parse(content)
  if (typeof parsed === 'string') return parsed
  if (!Array.isArray(parsed)) return typeof content === 'string' ? content : ''
  return joinType(content, 'text', 'text')
}

export function messageThinking(content: MessageRead['content']) {
  return joinType(content, 'thinking', 'thinking')
}

export function messageToolUses(content: MessageRead['content']): ToolUse[] {
  return messageBlocks(content)
    .filter((block) => block.type === 'tool_use')
    .map((block) => ({
      id: String(block.id ?? ''),
      name: String(block.name ?? 'tool'),
      displayName: typeof block.display_name === 'string' ? block.display_name : undefined,
    }))
}

export function messageToolResults(content: MessageRead['content']) {
  return messageBlocks(content)
    .filter((block) => block.type === 'tool_result' && block.tool_use_id)
    .map((block) => ({
      id: String(block.tool_use_id),
      result: {
        content: typeof block.content === 'string' ? block.content : JSON.stringify(block.content ?? ''),
        isError: Boolean(block.is_error),
      } satisfies ToolResult,
    }))
}

export function humanToolName(name: string) {
  return name.replace(/_/g, ' ')
}

/** Sub-agent replies are stored as ordinary messages tagged with the tool use that
 * spawned them; they belong inside that tool's reasoning box, not the transcript. */
export function groupSubMessages(messages: MessageRead[]) {
  const ownerByToolUse = new Map<string, string>()
  for (const message of messages) {
    if (message.parent_tool_use_id) continue
    for (const { id } of messageToolUses(message.content)) ownerByToolUse.set(id, message.id)
  }

  const byOwner = new Map<string, MessageRead[]>()
  for (const message of messages) {
    const owner = message.parent_tool_use_id && ownerByToolUse.get(message.parent_tool_use_id)
    if (!owner) continue
    byOwner.set(owner, [...(byOwner.get(owner) ?? []), message])
  }

  for (const list of byOwner.values()) list.sort((a, b) => a.sequence - b.sequence)
  return byOwner
}

/** Results keyed by tool use, minus the dispatchers whose output is already shown as
 * the sub-agent's own messages. */
export function collectToolResults(messages: MessageRead[]) {
  const dispatchers = new Set(
    messages.map(({ parent_tool_use_id }) => parent_tool_use_id).filter(Boolean),
  )

  const results = new Map<string, ToolResult>()
  for (const message of messages) {
    for (const { id, result } of messageToolResults(message.content)) {
      if (!dispatchers.has(id)) results.set(id, result)
    }
  }
  return results
}
